import * as Sentry from "@sentry/react";
import { CheckCircle2, ListChecks } from "lucide-react";
import { useCallback, useEffect, useState, type FC } from "react";

import { Button } from "@/components/ui/button";

import { getDraftById } from "../../../api/drafts-v2";
import {
  answersIssue,
  type Draft,
  type IssueScore
} from "../../../api/types/draft";
import { useOnInvalidate } from "../../caseIde/context/CaseIdeContext";

type Props = {
  draftId: string;
  /** Select the issue's heading in the open draft. */
  onShow: (title: string) => void;
};

const OPEN_LABEL: Partial<Record<IssueScore["status"], string>> = {
  missing: "Missing",
  needs_work: "Needs Work",
  needs_evidence: "Needs Evidence"
};

/**
 * Above a delivered RFE, NOID or NOIR response: how many of the notice's
 * issues it answers, and a jump to each one that still needs work.
 */
export const NoticeScoreStrip: FC<Props> = ({ draftId, onShow }) => {
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = useCallback(() => {
    getDraftById(draftId)
      .then(setDraft)
      .catch((error) => Sentry.captureException(error));
  }, [draftId]);

  useEffect(load, [load]);
  useOnInvalidate(["drafts"], load);

  const card = draft?.notice_scorecard;
  if (!card || !card.issues.length) return null;
  const answered = card.issues.filter(answersIssue).length;
  const pending = card.issues.filter((i) => i.status !== "answered");
  const label = draft?.notice_brief_ref?.label ?? "notice";

  return (
    <div
      data-testid="notice-score-strip"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-line-1 bg-fill-1 px-3 py-1.5 text-sm text-fg-2"
    >
      {answered < card.issues.length ? (
        <ListChecks className="size-3.5 shrink-0 text-warn" aria-hidden />
      ) : (
        <CheckCircle2 className="size-3.5 shrink-0 text-success" aria-hidden />
      )}
      <span className="text-fg">
        {answered} of {card.issues.length} {label} issues answered
      </span>
      {pending.map((issue) => (
        <Button
          key={issue.key}
          variant="link"
          size="xs"
          title={issue.findings.join("\n") || undefined}
          onClick={() => onShow(issue.title)}
        >
          {OPEN_LABEL[issue.status]}: {issue.title}
        </Button>
      ))}
    </div>
  );
};
